<template lang="pug">
#form.new-edit-form
  .spinner-container.form-spinner(v-show="loading")
    q-spinner(color="primary", size="3em")
  .form-card(v-show="!loading")
    component(
      v-if="formDataLoaded",
      :is="defaultForm",
      @fields-loaded="fieldsLoaded",
      @submit-form="submitForm",
      :parentData="{ method: 'update', grid, data: formData, formLoaded: !loading }"
    )
      template(v-slot:after)
        slot(name="after")
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import defaultForm from "../new_edit_form/forms/default_form";
import { useStore } from "@/store";
import { handleError } from "@/services/handleErrors";
import { useEmitter } from "@/services/useEmitter";
import backend from "@/api";
import { Notify } from "quasar";
import { notifies } from "@/services/useLocales";

const { normalizeCarPlatePayload } = require("@/services/useCarPlate");

const store = useStore();

const emitter = useEmitter();

const props = defineProps({
  /**
   * Текущий grid. Передается из index.vue текущей страницы. Пример: "users"
   */
  grid: { type: String, default: () => "" },
  /**
   * Дополнительные параметры для отправки запроса на бек. Пример: { id: 1, class_name: "user" }
   */
  additionalParams: { type: Object, default: () => {} },
  /**
   * Путь для запроса на бек. Пример: "/api/v3/users/form_data"
   */
  path: { type: String, default: () => "" },
  /**
   * id выбранного ряда. Пример: 1
   */
  rowId: { type: Number, default: () => null },
  /**
   * Дата формы загруженная от родителя. Принимает все данные пришедшие с запроса form_data
   */
  formData: { type: Object, default: () => null },
  /**
   * Проверка была ли составленна эта форма для профиля. Пример: false
   */
  isProfile: { type: Boolean, default: () => false },
});

const emit = defineEmits(["close-form"]);

const formData = ref({});
const loading = ref(true);
const formDataLoaded = ref(false);
const loadingFields = ref([]);
const formIsValid = ref(false);

const currentForm = computed(() => store.state.grid[props.grid]["form"]);

const setLoadingFields = () => {
  loadingFields.value = formData.value.fields
    .filter(f => store.state.fields_with_options.includes(f.type))
    .map(f => [f.type, f.name]);
};

const getFormData = async () => {
  try {
    loading.value = true;
    if (props.formData?.fields?.length > 0) {
      formData.value = props.formData;
    } else {
      const params = props.additionalParams;
      const { data } = await backend.index(`${props.path}`, { params });
      formData.value = data;
    }
  } catch (err) {
    await handleError(err);
  } finally {
    setLoadingFields();
    formDataLoaded.value = true;
    fieldsLoaded();
  }
};

onMounted(async () => {
  resetForm();
  await getFormData();
});

const getFormParams = () => {
  const form = currentForm.value;
  const params = {};

  const fieldsData = Object.keys(form).reduce((result, e) => {
    if (Array.isArray(form[e]["field"]) && form[e]["selected_items"]) {
      result[e] = form[e]["selected_items"];
    } else if (Array.isArray(form[e]["field"]) && e === "sensor_indicators") {
      result[e] = form[e]["field"];
    } else if (Array.isArray(form[e]["field"])) {
      result[e] = form[e]["field"].map(e => e["value"]);
    } else if (
      typeof form[e]["field"] === "object" &&
      form[e]["field"] !== null &&
      Object.prototype.hasOwnProperty.call(form[e]["field"], "plate_type")
    ) {
      result[e] = normalizeCarPlatePayload(form[e]["field"]);
    } else if (typeof form[e]["field"] === "object" && form[e]["field"] !== null) {
      result[e] = form[e]["field"]["value"];
    } else {
      result[e] = form[e]["field"];
    }
    return result;
  }, {});

  params.user = {
    ...fieldsData,
    ...(formData.value.additional_data || {}),
  };

  if (props.isProfile) {
    params["class_name"] = "profile";
  }

  return params;
};

const submitForm = async () => {
  checkFormValidation();

  if (formIsValid.value) {
    loading.value = true;
    const params = getFormParams();
    const path = store.state.paths.user;
    try {
      const res = await backend.update(path, props.rowId, params);

      if (res.data) {
        emit("close-form", {
          data: res.data,
          method: "update",
        });
      }
    } catch (err) {
      // Валидация неожиданных ошибок с бека при 422 на некоторых полях
      const baseErrors = err.response.data.errors.base;
      const fieldsErrors = err.response.data.errors;

      let customErrorsCount = 0;

      if (baseErrors) {
        Notify.create(baseErrors.map(el => el.message || el).join(". "));
      } else if (fieldsErrors) {
        Object.keys(fieldsErrors).forEach(e => {
          const fieldErrors = fieldsErrors[e];
          console.log(fieldErrors);

          if (fieldErrors) {
            makeFieldInvalid(e, fieldErrors.join(", "));
            customErrorsCount += 1;
          }
        });

        if (customErrorsCount > 0) {
          Notify.create(notifies.value.fix_invalid_fields);
        } else {
          // Обращаемся к классической обработке ошибок если с бека не пришли поля с ошибкой
          await handleError(err);
        }
      }
    } finally {
      loading.value = false;
    }
  } else {
    Notify.create(notifies.value.fix_invalid_fields);
  }
};

// Назначение поля инвалидным при 422 с бека и ошибкой на это поле
const makeFieldInvalid = (fieldName, message) => {
  const existingValue = currentForm.value[fieldName].field;
  const existingInvalidFields = currentForm.value.invalid_fields || {};
  const existingInvalidFieldsErrors = currentForm.value.invalid_fields_errors || {};

  // Set error state
  store.commit("updateFormField", {
    grid_name: props.grid,
    field: fieldName,
    value: { invalid: true, field: existingValue },
  });
  // Show red border
  store.commit("updateFormField", {
    grid_name: props.grid,
    field: "invalid_fields",
    value: { ...existingInvalidFields, [fieldName]: true },
  });

  // Set error message
  store.commit("updateFormField", {
    grid_name: props.grid,
    field: "invalid_fields_errors",
    value: { ...existingInvalidFieldsErrors, [fieldName]: message },
  });
};

const checkFormValidation = () => {
  const form = currentForm.value;
  const invalidForm = Object.keys(form).reduce((result, e) => {
    if (form[e]["invalid"] === true) result[e] = form[e];
    return result;
  }, {});

  const invalidFields = Object.keys(invalidForm);

  if (invalidFields.length > 0) {
    formIsValid.value = false;

    store.commit("createFormField", { grid_name: props.grid, field: "invalid_fields" });

    let val = {};
    invalidFields.forEach(f => (val[f] = true));
    store.commit("updateFormField", { grid_name: props.grid, field: "invalid_fields", value: val });
  } else {
    formIsValid.value = true;
    store.commit("resetFormField", { grid_name: props.grid, field: "invalid_fields" });
  }
};

const resetForm = () => {
  const fields = Object.keys(currentForm.value);
  store.commit("resetFormAllFields", { grid_name: props.grid, fields: fields });
};

const fieldsLoaded = (field = undefined) => {
  if (field) {
    loadingFields.value = loadingFields.value.filter(f => f.sort().toString() !== field.sort().toString());
  }

  if (loadingFields.value.length === 0) {
    loading.value = false;
    emitter.emit("allFieldsLoaded");
  }
};
</script>

<style lang="scss" scoped>
.new-edit-form {
  position: relative;

  .spinner-container {
    position: relative;
    // 128 пикселей это высота хедера и табов
    height: calc(100vh - 128px);
  }

  .form-card {
    padding: 16px 16px 0 16px;
  }
}
</style>
